/**
 * OutreachPage — Reach Command Center
 *
 * Campaign-grouped message review with:
 * - Landing: campaigns grouped by saved search with per-campaign stats
 * - Review: two-pane message inbox with click-to-edit, mailto send, keyboard shortcuts
 * - Table: bulk operations view
 *
 * Backend: GET /api/ai/outreach-history, PATCH /api/ai/outreach/:id/draft
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Send, Mail, Linkedin, Copy, Loader2, RefreshCw,
  ChevronRight, ChevronLeft, Sparkles,
  Zap, Lightbulb,
  Mic, MapPin, Handshake, Briefcase, CalendarDays, UserPlus,
  MessageCircle, Target, Table2, LayoutList, Plus, Users,
  ArrowRight, Check,
} from 'lucide-react';
import api from '../../utils/axios';
import { useIsMobile } from '../../hooks/useMediaQuery';

// ---------------------------------------------------------------------------
// OUTREACH ANGLES — mirrors backend INTENT_PROMPTS
// ---------------------------------------------------------------------------
const ANGLES = [
  { id: 'sell',          label: 'Sell',           icon: Target,        context: 'sales',      color: '#5a20ff' },
  { id: 'intro',         label: 'Intro',          icon: MessageCircle, context: 'networking',  color: '#3b82f6' },
  { id: 'podcast',       label: 'Podcast',        icon: Mic,           context: 'content',     color: '#ec4899' },
  { id: 'local',         label: 'Local',          icon: MapPin,        context: 'networking',  color: '#f59e0b' },
  { id: 'partner',       label: 'Partner',        icon: Handshake,     context: 'partnership', color: '#10b981' },
  { id: 'recruit',       label: 'Recruit',        icon: Briefcase,     context: 'recruiting',  color: '#8b5cf6' },
  { id: 'event',         label: 'Event',          icon: CalendarDays,  context: 'networking',  color: '#06b6d4' },
  { id: 'value_first',   label: 'Value-First',    icon: Sparkles,      context: 'sales',      color: '#52dcb3' },
  { id: 'intro_request', label: 'Intro Request',  icon: UserPlus,      context: 'networking',  color: '#6366f1' },
  { id: 'follow_up',     label: 'Follow-up',      icon: RefreshCw,     context: 'sales',      color: '#64748b' },
];

const STAGES = [
  { id: 'draft',   label: 'Draft',   color: '#5a20ff' },
  { id: 'sent',    label: 'Sent',    color: '#3b82f6' },
  { id: 'opened',  label: 'Opened',  color: '#10b981' },
];

const HOOK_BADGES = {
  funding:   { label: 'Raised Funding', color: '#10b981' },
  expansion: { label: 'Expanding',      color: '#3b82f6' },
  hiring:    { label: 'Hiring',         color: '#8b5cf6' },
  news:      { label: 'In the News',    color: '#f59e0b' },
  new_role:  { label: 'New Role',       color: '#ec4899' },
  general:   { label: 'Matched Search', color: '#64748b' },
};

// ---------------------------------------------------------------------------
// HELPERS
// ---------------------------------------------------------------------------
function timeAgo(date) {
  if (!date) return '';
  const now = new Date();
  const then = new Date(date);
  const diff = now - then;
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  if (hours < 24) return `${hours}h`;
  if (days < 7) return `${days}d`;
  return then.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function cleanEmail(raw) {
  if (!raw || typeof raw !== 'string') return '';
  const cleaned = raw.replace(/[{}"[\]]/g, '').trim();
  if (!cleaned.includes('@')) return '';
  // If it contains commas or semicolons, return just the first valid address
  const parts = cleaned.split(/[,;]\s*/);
  const first = parts.find(p => p.trim().includes('@'));
  return first ? first.trim() : '';
}

function extractAllEmails(data) {
  const raw = [data.email, data.workEmail, data.personalEmail, data.work_email]
    .filter(Boolean)
    .map(e => typeof e === 'string' ? e : '');
  const all = [];
  for (const r of raw) {
    const cleaned = r.replace(/[{}"[\]]/g, '').trim();
    // Split comma/semicolon-separated values
    const parts = cleaned.split(/[,;]\s*/);
    for (const p of parts) {
      const addr = p.trim();
      if (addr.includes('@') && !all.includes(addr)) all.push(addr);
    }
  }
  return all;
}

function extractContact(item) {
  const d = item.contact || item.leadData || {};
  const emails = extractAllEmails(d);
  return {
    name: d.name || d.fullName || [d.firstName, d.lastName].filter(Boolean).join(' ') || 'Unknown',
    firstName: d.firstName || d.first_name || (d.name || '').split(' ')[0] || 'there',
    lastName: d.lastName || d.last_name || '',
    email: emails[0] || '',
    emails,
    linkedinUrl: d.linkedin_url || d.linkedinUrl || '',
    title: d.title || d.jobTitle || d.job_title || '',
    company: d.companyName || d.company_name || d.company || '',
    industry: d.industry || '',
    location: d.location || d.city || '',
  };
}

// ---------------------------------------------------------------------------
// PIPELINE BAR
// ---------------------------------------------------------------------------
function PipelineBar({ stats, activeStage, onStageClick }) {
  const researching = stats.researching || 0;
  return (
    <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide">
      {researching > 0 && (
        <>
          <span className="flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-amber-600 whitespace-nowrap">
            <Loader2 className="w-3 h-3 animate-spin" />
            {researching} generating
          </span>
          <ChevronRight className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" />
        </>
      )}
      {STAGES.map((stage, i) => {
        const count = stats[stage.id] || 0;
        const isActive = activeStage === stage.id;
        return (
          <React.Fragment key={stage.id}>
            {i > 0 && <ChevronRight className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" />}
            <button
              onClick={() => onStageClick(isActive ? 'all' : stage.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                isActive
                  ? 'ring-2 ring-offset-1 text-white'
                  : 'hover:bg-slate-100 text-slate-600'
              }`}
              style={isActive ? { backgroundColor: stage.color, ringColor: stage.color } : {}}
            >
              <span className={`text-xs font-bold ${isActive ? 'text-white/90' : ''}`}
                    style={!isActive ? { color: stage.color } : {}}>
                {count}
              </span>
              {stage.label}
            </button>
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// CAMPAIGN CARD (landing page)
// ---------------------------------------------------------------------------
function CampaignCard({ campaign, onClick }) {
  const drafts = campaign.draft || 0;
  const sent = campaign.sent || 0;
  const opened = campaign.opened || 0;
  const total = campaign.total || 0;

  return (
    <button
      onClick={onClick}
      className="w-full text-left p-5 bg-white rounded-xl border border-slate-200 hover:border-[#5a20ff]/30 hover:shadow-md transition-all group"
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#5a20ff]/10 to-[#52dcb3]/10 flex items-center justify-center flex-shrink-0">
            <Users className="w-5 h-5 text-[#5a20ff]" />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-slate-900 truncate group-hover:text-[#5a20ff] transition-colors">
              {campaign.name}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">{total} contact{total !== 1 ? 's' : ''}</p>
          </div>
        </div>
        <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-[#5a20ff] transition-colors flex-shrink-0 mt-1" />
      </div>

      <div className="flex items-center gap-4 mt-3 text-xs">
        {drafts > 0 && (
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-[#5a20ff]" />
            <span className="font-semibold text-[#5a20ff]">{drafts}</span>
            <span className="text-slate-400">draft{drafts !== 1 ? 's' : ''}</span>
          </span>
        )}
        {sent > 0 && (
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-blue-500" />
            <span className="font-semibold text-blue-500">{sent}</span>
            <span className="text-slate-400">sent</span>
          </span>
        )}
        {opened > 0 && (
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-emerald-500" />
            <span className="font-semibold text-emerald-500">{opened}</span>
            <span className="text-slate-400">opened</span>
          </span>
        )}
        {(campaign.researching || 0) > 0 && (
          <span className="flex items-center gap-1">
            <Loader2 className="w-3 h-3 text-amber-500 animate-spin" />
            <span className="font-semibold text-amber-500">{campaign.researching}</span>
            <span className="text-slate-400">generating</span>
          </span>
        )}
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// QUEUE ITEM (left pane row)
// ---------------------------------------------------------------------------
function QueueItem({ item, isSelected, onClick }) {
  const c = extractContact(item);
  const hookInfo = HOOK_BADGES[item.hookType] || HOOK_BADGES.general;
  const stageInfo = STAGES.find(s => s.id === item.status) || STAGES[0];
  const isResearching = item.status === 'researching';

  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-4 py-3 border-b border-slate-100 transition-all ${
        isSelected
          ? 'bg-[#5a20ff]/5 border-l-2 border-l-[#5a20ff]'
          : 'hover:bg-slate-50 border-l-2 border-l-transparent'
      } ${isResearching ? 'opacity-60' : ''}`}
    >
      <div className="flex items-start gap-3">
        <div className="relative flex-shrink-0">
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-semibold"
            style={{ background: `linear-gradient(135deg, #5a20ff, #52dcb3)` }}
          >
            {c.name.charAt(0).toUpperCase()}
          </div>
          {isResearching && (
            <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-white rounded-full flex items-center justify-center">
              <Loader2 className="w-3 h-3 text-amber-500 animate-spin" />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-slate-900 truncate">{c.name}</p>
            <span className="text-[10px] text-slate-400 flex-shrink-0 ml-2">
              {isResearching ? 'generating...' : item.generatedAt || item.sentAt ? timeAgo(item.generatedAt || item.sentAt) : 'new'}
            </span>
          </div>
          <p className="text-xs text-slate-500 truncate">
            {c.title}{c.title && c.company ? ' · ' : ''}{c.company}
          </p>
          <div className="flex items-center gap-1.5 mt-1">
            {item.hook && (
              <span
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium"
                style={{ backgroundColor: hookInfo.color + '18', color: hookInfo.color }}
              >
                <Zap className="w-2.5 h-2.5" />
                {item.hook.length > 30 ? item.hook.slice(0, 30) + '…' : item.hook}
              </span>
            )}
            {!isResearching && (
              <span
                className="px-1.5 py-0.5 rounded text-[10px] font-semibold"
                style={{ backgroundColor: stageInfo.color + '18', color: stageInfo.color }}
              >
                {stageInfo.label}
              </span>
            )}
          </div>
        </div>
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// ANGLE PICKER (compact)
// ---------------------------------------------------------------------------
function AnglePicker({ selected, onChange }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {ANGLES.map(a => {
        const Icon = a.icon;
        const isActive = selected === a.id;
        return (
          <button
            key={a.id}
            onClick={() => onChange(a.id)}
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
              isActive
                ? 'text-white shadow-sm'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
            style={isActive ? { backgroundColor: a.color } : {}}
            title={a.label}
          >
            <Icon className="w-3 h-3" />
            {a.label}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// EMAIL PREVIEW (right pane)
// ---------------------------------------------------------------------------
function EmailPreview({ item, onEdit, onRegenerate, onSend, onSkip, onAngleChange, onGenerate, isRegenerating, onBack }) {
  const c = extractContact(item);
  const hookInfo = HOOK_BADGES[item.hookType] || HOOK_BADGES.general;
  const angleInfo = ANGLES.find(a => a.id === item.angle) || ANGLES[0];
  const needsGeneration = item.status === 'draft' && !item.body;

  const [isEditing, setIsEditing] = useState(false);
  const [editSubject, setEditSubject] = useState(item.subject || '');
  const [editBody, setEditBody] = useState(item.body || '');
  const [copied, setCopied] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(c.emails[0] || '');
  const textareaRef = useRef(null);

  // Keep selectedEmail in sync when switching leads
  useEffect(() => {
    setSelectedEmail(c.emails[0] || '');
  }, [item.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Deep Dive research state
  const [research, setResearch] = useState(null);
  const [isResearchLoading, setIsResearchLoading] = useState(false);
  const [showResearch, setShowResearch] = useState(true);

  // Auto-fetch research when a lead is selected and has no research yet
  useEffect(() => {
    if (!c.company) return;
    const cached = item.leadData?.insights;
    if (cached?.whyNow) {
      setResearch({ whyNow: cached.whyNow, suggestedOpener: cached.suggestedOpener, confidence: cached.confidence, openJobs: cached.openJobs });
      return;
    }
    if (item.hook) {
      setResearch({ whyNow: item.hook, confidence: 'medium' });
      return;
    }
    setResearch(null);
  }, [item.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchResearch = async () => {
    setIsResearchLoading(true);
    try {
      const res = await api.post('/api/ai/why-now', {
        contact: { name: c.name, title: c.title, company: c.company, linkedin: c.linkedinUrl },
        companyContext: { industry: c.industry },
      });
      if (res.data?.success) {
        setResearch(res.data);
      }
    } catch (err) {
      console.warn('Research fetch failed:', err.message);
    } finally {
      setIsResearchLoading(false);
    }
  };

  useEffect(() => {
    setEditSubject(item.subject || '');
    setEditBody(item.body || '');
    setIsEditing(false);
    setCopied(false);
  }, [item.id, item.subject, item.body]);

  const handleStartEdit = () => {
    setIsEditing(true);
    setTimeout(() => textareaRef.current?.focus(), 50);
  };

  const handleSaveEdit = () => {
    setIsEditing(false);
    if (onEdit) onEdit(item.id, editSubject, editBody);
  };

  const handleCopy = async () => {
    const body = isEditing ? editBody : (item.body || '');
    await navigator.clipboard.writeText(body);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleLinkedIn = () => {
    const body = isEditing ? editBody : (item.body || '');
    navigator.clipboard.writeText(body.substring(0, 300));
    if (c.linkedinUrl) window.open(c.linkedinUrl, '_blank');
    if (onSend) onSend(item.id);
  };

  const isSent = item.status === 'sent' || item.status === 'opened';
  const isReady = item.status === 'draft';
  const isResearching = item.status === 'researching';

  // Build compose URLs reactively from current state
  const composeBody = isEditing ? editBody : (item.body || '');
  const composeSubject = isEditing ? editSubject : (item.subject || '');

  const gmailComposeUrl = selectedEmail
    ? `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(selectedEmail)}&su=${encodeURIComponent(composeSubject)}&body=${encodeURIComponent(composeBody)}`
    : null;

  const outlookComposeUrl = selectedEmail
    ? `https://outlook.office.com/mail/deeplink/compose?to=${encodeURIComponent(selectedEmail)}&subject=${encodeURIComponent(composeSubject)}&body=${encodeURIComponent(composeBody)}`
    : null;

  const handleSendClick = () => {
    if (isEditing) handleSaveEdit();
    setTimeout(() => {
      if (onSend) onSend(item.id, 'email');
    }, 300);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Contact header */}
      <div className="px-4 sm:px-6 py-4 border-b border-slate-100 flex-shrink-0">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            {onBack && (
              <button onClick={onBack} className="p-1 -ml-1 hover:bg-slate-100 rounded-lg transition-colors sm:hidden">
                <ChevronLeft className="w-5 h-5 text-slate-400" />
              </button>
            )}
            <div
              className="w-11 h-11 rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #5a20ff, #52dcb3)' }}
            >
              {c.name.charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-slate-900 truncate" style={{ fontFamily: "'Playfair Display', serif" }}>
                {c.name}
              </h2>
              <p className="text-sm text-slate-500 truncate">
                {c.title}{c.title && c.company ? ' at ' : ''}{c.company}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-400 flex-wrap">
            {c.emails.length > 1 ? (
              <select
                value={selectedEmail}
                onChange={(e) => setSelectedEmail(e.target.value)}
                className="flex items-center gap-1 px-2 py-1 bg-slate-50 rounded-lg text-xs text-slate-600 border-none focus:outline-none focus:ring-2 focus:ring-[#5a20ff]/20 cursor-pointer appearance-none pr-5"
                style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 6px center' }}
              >
                {c.emails.map((em) => (
                  <option key={em} value={em}>{em}</option>
                ))}
              </select>
            ) : c.email ? (
              <span className="flex items-center gap-1 px-2 py-1 bg-slate-50 rounded-lg truncate max-w-[200px] sm:max-w-none">
                <Mail className="w-3 h-3 flex-shrink-0" /> {c.email}
              </span>
            ) : null}
            {c.linkedinUrl && (
              <a href={c.linkedinUrl} target="_blank" rel="noopener noreferrer"
                 className="flex items-center gap-1 px-2 py-1 bg-slate-50 rounded-lg hover:bg-blue-50 hover:text-blue-600 transition-colors">
                <Linkedin className="w-3 h-3" /> LinkedIn
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Hook context */}
      {item.hook && (
        <div className="mx-6 mt-4 p-3 rounded-xl border flex items-start gap-3"
             style={{ backgroundColor: hookInfo.color + '08', borderColor: hookInfo.color + '25' }}>
          <Zap className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: hookInfo.color }} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-800">{item.hook}</p>
            {item.hookType && (
              <p className="text-xs mt-0.5" style={{ color: hookInfo.color }}>
                {hookInfo.label}
              </p>
            )}
          </div>
          <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold text-white flex-shrink-0"
                style={{ backgroundColor: angleInfo.color }}>
            {React.createElement(angleInfo.icon, { className: 'w-2.5 h-2.5' })}
            {angleInfo.label}
          </span>
        </div>
      )}

      {/* Scrollable message area */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        {isResearching ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <Loader2 className="w-8 h-8 text-[#5a20ff] animate-spin mb-3" />
            <p className="text-sm font-medium text-slate-700">Generating message for {c.name}...</p>
            <p className="text-xs text-slate-500 mt-1">Finding the perfect hook and crafting a personalised email</p>
          </div>
        ) : needsGeneration ? (
          /* No draft yet — show research + angle picker + generate */
          <div className="space-y-5">
            {/* Deep Dive Research */}
            <div className="bg-gradient-to-br from-[#5a20ff]/[0.03] to-[#52dcb3]/[0.03] rounded-xl border border-slate-100 p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                  <Lightbulb className="w-3 h-3" /> Deep Dive
                </p>
                {research && (
                  <button onClick={fetchResearch} disabled={isResearchLoading}
                    className="text-[10px] text-[#5a20ff] hover:underline flex items-center gap-1">
                    <RefreshCw className={`w-2.5 h-2.5 ${isResearchLoading ? 'animate-spin' : ''}`} /> Refresh
                  </button>
                )}
              </div>

              {!research && !isResearchLoading && (
                <button onClick={fetchResearch}
                  className="w-full py-3 bg-gradient-to-r from-[#5a20ff]/10 to-[#52dcb3]/10 text-[#5a20ff] rounded-lg text-sm font-medium hover:from-[#5a20ff]/15 hover:to-[#52dcb3]/15 transition-all flex items-center justify-center gap-2">
                  <Zap className="w-4 h-4" /> Research {c.company || c.name}
                </button>
              )}

              {isResearchLoading && (
                <div className="flex items-center justify-center py-4 gap-2">
                  <Loader2 className="w-4 h-4 text-[#5a20ff] animate-spin" />
                  <span className="text-xs text-slate-500">Researching {c.company}...</span>
                </div>
              )}

              {research && !isResearchLoading && (
                <div className="space-y-2">
                  {research.confidence && (
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                      research.confidence === 'high' ? 'bg-emerald-100 text-emerald-700' :
                      research.confidence === 'medium' ? 'bg-amber-100 text-amber-700' :
                      'bg-slate-100 text-slate-600'
                    }`}>
                      {research.confidence === 'high' ? 'High Signal' : research.confidence === 'medium' ? 'Medium' : 'Low Signal'}
                    </span>
                  )}
                  <p className="text-sm text-slate-700 leading-relaxed">{research.whyNow}</p>
                  {research.suggestedOpener && (
                    <div className="p-2.5 bg-white rounded-lg border border-slate-100">
                      <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Suggested opener</p>
                      <p className="text-sm text-slate-800 italic">"{research.suggestedOpener}"</p>
                    </div>
                  )}
                  {research.openJobs > 0 && (
                    <p className="text-xs text-emerald-600 flex items-center gap-1">
                      <Briefcase className="w-3 h-3" /> {research.openJobs} open positions at {c.company}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Angle picker */}
            <div>
              <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wider mb-2">Positioning</p>
              <AnglePicker selected={item.angle || 'sell'} onChange={(a) => onAngleChange?.(item.id, a)} />
            </div>

            {/* Generate button */}
            <div className="text-center">
              <button
                onClick={() => onGenerate?.(item.id, research)}
                disabled={isRegenerating}
                className="flex items-center gap-2 px-6 py-3 mx-auto bg-gradient-to-r from-[#5a20ff] to-[#52dcb3] text-white font-semibold rounded-xl hover:opacity-90 disabled:opacity-50 transition-all shadow-lg shadow-[#5a20ff]/20"
              >
                {isRegenerating ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Generating...</>
                ) : (
                  <><Sparkles className="w-4 h-4" /> Generate Email</>
                )}
              </button>
              {!c.email && (
                <p className="text-xs text-amber-500 mt-2">No email address found — you can still generate and copy</p>
              )}
            </div>
          </div>
        ) : (
          <>
            {/* Collapsible research summary */}
            {isReady && research && (
              <button onClick={() => setShowResearch(!showResearch)}
                className="w-full mb-3 p-3 rounded-xl bg-gradient-to-r from-[#5a20ff]/[0.04] to-[#52dcb3]/[0.04] border border-slate-100 text-left hover:border-slate-200 transition-colors">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                    <Lightbulb className="w-3 h-3" /> Research
                    {research.confidence && (
                      <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[9px] ${
                        research.confidence === 'high' ? 'bg-emerald-100 text-emerald-700' :
                        research.confidence === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'
                      }`}>{research.confidence === 'high' ? 'High' : research.confidence === 'medium' ? 'Med' : 'Low'}</span>
                    )}
                  </p>
                  <ChevronRight className={`w-3 h-3 text-slate-400 transition-transform ${showResearch ? 'rotate-90' : ''}`} />
                </div>
                {showResearch && (
                  <div className="mt-2 space-y-1.5" onClick={(e) => e.stopPropagation()}>
                    <p className="text-xs text-slate-600 leading-relaxed">{research.whyNow}</p>
                    {research.suggestedOpener && (
                      <p className="text-xs text-slate-500 italic">"{research.suggestedOpener}"</p>
                    )}
                  </div>
                )}
              </button>
            )}

            {/* Angle picker + regenerate */}
            {isReady && (
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Positioning</p>
                  <button
                    onClick={() => onRegenerate?.(item.id)}
                    disabled={isRegenerating}
                    className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-slate-500 hover:text-[#5a20ff] hover:bg-[#5a20ff]/5 rounded-lg transition-colors disabled:opacity-50"
                  >
                    <RefreshCw className={`w-3 h-3 ${isRegenerating ? 'animate-spin' : ''}`} />
                    {isRegenerating ? 'Regenerating...' : 'Regenerate'}
                  </button>
                </div>
                <AnglePicker selected={item.angle || 'sell'} onChange={(a) => onAngleChange?.(item.id, a)} />
              </div>
            )}

            {/* Email card — click body to edit */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-5 py-3 bg-slate-50 border-b border-slate-100 text-xs text-slate-500 space-y-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-400 w-10">To:</span>
                  <span className="text-slate-700">{selectedEmail || c.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-400 w-10">Subj:</span>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editSubject}
                      onChange={(e) => setEditSubject(e.target.value)}
                      className="flex-1 text-sm font-medium text-slate-900 bg-transparent border-b border-[#5a20ff] focus:outline-none py-0.5"
                    />
                  ) : (
                    <span
                      className="text-sm font-medium text-slate-900 cursor-text hover:bg-[#5a20ff]/5 px-1 -mx-1 rounded transition-colors"
                      onClick={isReady ? handleStartEdit : undefined}
                    >
                      {item.subject || '(no subject)'}
                    </span>
                  )}
                </div>
              </div>

              {/* Email body — click to edit */}
              <div className="px-5 py-4">
                {isEditing ? (
                  <div>
                    <textarea
                      ref={textareaRef}
                      value={editBody}
                      onChange={(e) => setEditBody(e.target.value)}
                      className="w-full text-sm text-slate-700 leading-relaxed resize-none focus:outline-none min-h-[200px] border-2 border-[#5a20ff]/30 rounded-lg p-3 focus:border-[#5a20ff]"
                      rows={10}
                    />
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        onClick={handleSaveEdit}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-[#5a20ff] text-white text-xs font-medium rounded-lg hover:bg-[#4a18d9] transition-colors"
                      >
                        <Check className="w-3 h-3" /> Save edits
                      </button>
                      <button
                        onClick={() => { setEditSubject(item.subject || ''); setEditBody(item.body || ''); setIsEditing(false); }}
                        className="px-3 py-1.5 text-xs text-slate-500 hover:text-slate-700 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    className={`text-sm text-slate-700 leading-relaxed whitespace-pre-wrap ${isReady ? 'cursor-text hover:bg-[#5a20ff]/[0.02] rounded-lg p-2 -m-2 transition-colors' : ''}`}
                    onClick={isReady ? handleStartEdit : undefined}
                  >
                    {item.body || 'No message generated yet.'}
                    {isReady && (
                      <p className="text-[10px] text-slate-300 mt-3 italic">Click to edit</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Sent/Opened state info */}
            {isSent && (
              <div className="mt-4 p-3 bg-slate-50 rounded-xl text-sm text-slate-500">
                <div className="flex items-center gap-4">
                  {item.sentAt && (
                    <span className="flex items-center gap-1">
                      <Send className="w-3.5 h-3.5" /> Sent {timeAgo(item.sentAt)}
                    </span>
                  )}
                  {item.status === 'opened' && (
                    <span className="flex items-center gap-1 text-emerald-600">
                      <Mail className="w-3.5 h-3.5" /> Opened
                    </span>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer actions */}
      {isReady && !isResearching && (
        <div className="px-4 sm:px-6 py-4 border-t border-slate-100 bg-slate-50/50 flex-shrink-0">
          {selectedEmail ? (
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              <a href={gmailComposeUrl} target="_blank" rel="noopener noreferrer" onClick={handleSendClick}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-white border border-slate-200 text-slate-800 font-semibold rounded-xl hover:border-red-300 hover:bg-red-50 transition-all no-underline">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none">
                  <path d="M22 6L12 13L2 6" stroke="#EA4335" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <rect x="2" y="4" width="20" height="16" rx="2" stroke="#4285F4" strokeWidth="2"/>
                </svg>
                Send with Gmail
              </a>
              <a href={outlookComposeUrl} target="_blank" rel="noopener noreferrer" onClick={handleSendClick}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-white border border-slate-200 text-slate-800 font-semibold rounded-xl hover:border-blue-300 hover:bg-blue-50 transition-all no-underline">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none">
                  <rect x="2" y="4" width="20" height="16" rx="2" stroke="#0078D4" strokeWidth="2"/>
                  <path d="M2 6L12 13L22 6" stroke="#0078D4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Send with Outlook
              </a>
            </div>
          ) : (
            <p className="text-sm text-center text-slate-400 py-2">No email address available for this contact</p>
          )}

          {/* Secondary actions row */}
          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center gap-1">
              <button
                onClick={handleCopy}
                className="flex items-center gap-1 px-2 py-1 text-xs text-slate-400 hover:text-[#5a20ff] hover:bg-[#5a20ff]/5 rounded-lg transition-colors"
                title="Copy to clipboard"
              >
                {copied ? <><Check className="w-3 h-3 text-emerald-500" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
              </button>

              {c.linkedinUrl && (
                <button
                  onClick={handleLinkedIn}
                  className="flex items-center gap-1 px-2 py-1 text-xs text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  title="Copy & open LinkedIn"
                >
                  <Linkedin className="w-3 h-3" /> LinkedIn
                </button>
              )}

              <button
                onClick={() => onSkip?.(item.id)}
                className="flex items-center gap-1 px-2 py-1 text-xs text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              >
                Skip
              </button>
            </div>

            <p className="text-[10px] text-slate-400 hidden sm:flex items-center gap-2">
              <span><kbd className="px-1 py-0.5 bg-slate-200 rounded text-[9px]">⌘↵</kbd> Send</span>
              <span><kbd className="px-1 py-0.5 bg-slate-200 rounded text-[9px]">E</kbd> Edit</span>
              <span><kbd className="px-1 py-0.5 bg-slate-200 rounded text-[9px]">R</kbd> Regen</span>
              <span><kbd className="px-1 py-0.5 bg-slate-200 rounded text-[9px]">X</kbd> Skip</span>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// TABLE VIEW
// ---------------------------------------------------------------------------
function TableView({ items, onSelect, selectedId }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-left">
            <th className="py-2.5 px-4 font-medium text-slate-500">Contact</th>
            <th className="py-2.5 px-4 font-medium text-slate-500">Company</th>
            <th className="py-2.5 px-4 font-medium text-slate-500">Hook</th>
            <th className="py-2.5 px-4 font-medium text-slate-500">Status</th>
            <th className="py-2.5 px-4 font-medium text-slate-500">Subject</th>
            <th className="py-2.5 px-4 font-medium text-slate-500">Date</th>
          </tr>
        </thead>
        <tbody>
          {items.map(item => {
            const c = extractContact(item);
            const stageInfo = STAGES.find(s => s.id === item.status) || STAGES[1];
            const hookInfo = HOOK_BADGES[item.hookType] || HOOK_BADGES.general;
            return (
              <tr
                key={item.id}
                onClick={() => onSelect(item)}
                className={`border-b border-slate-50 cursor-pointer transition-colors ${
                  selectedId === item.id ? 'bg-[#5a20ff]/5' : 'hover:bg-slate-50'
                }`}
              >
                <td className="py-2.5 px-4">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0"
                         style={{ background: 'linear-gradient(135deg, #5a20ff, #52dcb3)' }}>
                      {c.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900 truncate max-w-[160px]">{c.name}</p>
                      <p className="text-xs text-slate-400 truncate max-w-[160px]">{c.title}</p>
                    </div>
                  </div>
                </td>
                <td className="py-2.5 px-4 text-slate-600 truncate max-w-[140px]">{c.company}</td>
                <td className="py-2.5 px-4">
                  {item.hook && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium"
                          style={{ backgroundColor: hookInfo.color + '18', color: hookInfo.color }}>
                      {item.hook.length > 25 ? item.hook.slice(0, 25) + '…' : item.hook}
                    </span>
                  )}
                </td>
                <td className="py-2.5 px-4">
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold"
                        style={{ backgroundColor: stageInfo.color + '18', color: stageInfo.color }}>
                    {stageInfo.label}
                  </span>
                </td>
                <td className="py-2.5 px-4 text-slate-500 truncate max-w-[180px]">{item.subject || '—'}</td>
                <td className="py-2.5 px-4 text-slate-400 text-xs">{timeAgo(item.generatedAt || item.sentAt)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ===========================================================================
// MAIN: OutreachPage
// ===========================================================================
export default function OutreachPage() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [items, setItems] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [stats, setStats] = useState({ researching: 0, draft: 0, sent: 0, opened: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [activeStage, setActiveStage] = useState('all');
  const [selectedItem, setSelectedItem] = useState(null);
  const [activeCampaign, setActiveCampaign] = useState(null); // null = landing page, 'all' = flat queue, id = campaign
  const [viewMode, setViewMode] = useState('review');
  const [mobileShowPreview, setMobileShowPreview] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [hasBrandVoice, setHasBrandVoice] = useState(true); // assume true to avoid flash
  const [brandNudgeDismissed, setBrandNudgeDismissed] = useState(false);
  const queueRef = useRef(null);

  // ---------------------------------------------------------------------------
  // FETCH DATA
  // ---------------------------------------------------------------------------
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await api.get('/api/ai/outreach-history', {
        params: { status: activeStage === 'all' ? undefined : activeStage },
      });

      if (res.data?.success) {
        const history = (res.data.history || []).map(h => ({
          id: h.id,
          status: h.status,
          subject: h.subject,
          body: h.body,
          hook: h.hook,
          hookType: h.hookType,
          angle: h.angle || 'sell',
          generatedAt: h.generatedAt,
          sentAt: h.sentAt,
          repliedAt: h.repliedAt,
          contact: h.contact || h.leadData || {},
          leadData: h.leadData || h.contact || {},
          searchName: h.searchName,
          savedSearchId: h.savedSearchId,
        }));
        setItems(history);

        if (res.data.campaigns) {
          setCampaigns(res.data.campaigns);
        }

        if (res.data.hasBrandVoice !== undefined) {
          setHasBrandVoice(res.data.hasBrandVoice);
        }

        if (res.data.stats) {
          setStats({
            researching: res.data.stats.researching || 0,
            draft: res.data.stats.draft || 0,
            sent: res.data.stats.sent || 0,
            opened: res.data.stats.opened || 0,
          });
        }

        setSelectedItem(prev => (prev ? prev : history[0] || null));
      }
    } catch (err) {
      console.error('Failed to fetch outreach data:', err);
      setError('Failed to load outreach data');
    } finally {
      setIsLoading(false);
    }
  }, [activeStage]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchData(); }, [fetchData]);

  // ---------------------------------------------------------------------------
  // FILTERED ITEMS
  // ---------------------------------------------------------------------------
  const filteredItems = useMemo(() => {
    let result = items;
    if (activeStage !== 'all') {
      result = result.filter(i => i.status === activeStage);
    }
    if (activeCampaign && activeCampaign !== 'all') {
      result = result.filter(i => String(i.savedSearchId) === String(activeCampaign));
    }
    return result;
  }, [items, activeStage, activeCampaign]);

  // ---------------------------------------------------------------------------
  // ACTIONS
  // ---------------------------------------------------------------------------
  const handleSend = (itemId) => {
    // Mailto is handled by the native <a> tag — this only does bookkeeping
    api.patch(`/api/ai/outreach/${itemId}/status`, { status: 'sent' }).catch(err => {
      console.warn('Failed to update outreach status:', err.message);
    });

    setItems(prev => prev.map(i =>
      i.id === itemId ? { ...i, status: 'sent', sentAt: new Date().toISOString() } : i
    ));
    setStats(prev => ({ ...prev, draft: Math.max(0, prev.draft - 1), sent: prev.sent + 1 }));

    const currentIdx = filteredItems.findIndex(i => i.id === itemId);
    const nextItem = filteredItems[currentIdx + 1];
    if (nextItem) setSelectedItem(nextItem);
  };

  const handleSkip = async (itemId) => {
    try {
      await api.delete(`/api/ai/outreach/${itemId}`);
      setItems(prev => prev.filter(i => i.id !== itemId));
      setStats(prev => ({ ...prev, draft: Math.max(0, prev.draft - 1) }));

      const currentIdx = filteredItems.findIndex(i => i.id === itemId);
      const nextItem = filteredItems[currentIdx + 1] || filteredItems[currentIdx - 1];
      setSelectedItem(nextItem || null);
    } catch (err) {
      console.error('Skip failed:', err);
    }
  };

  const handleRegenerate = async (itemId) => {
    setIsRegenerating(true);
    try {
      const item = items.find(i => i.id === itemId);
      const c = extractContact(item);
      const angle = item.angle || 'sell';
      const angleInfo = ANGLES.find(a => a.id === angle);
      const insights = item.leadData?.insights || {};

      const res = await api.post('/api/ai/generate-outreach', {
        contact: { name: c.name, title: c.title, company: c.company, industry: c.industry, location: c.location },
        intent: angle,
        outreachContext: angleInfo?.context || 'sales',
        generateMultiple: false,
        leadId: itemId,
        hook: item.hook,
        hookType: item.hookType,
        webContext: {
          recentNews: insights.newsSummary || '',
          openPositions: insights.totalJobs ? `${insights.totalJobs} open positions` : '',
          isHiring: insights.totalJobs > 0,
        },
      });

      if (res.data?.success) {
        let newBody = typeof res.data.message === 'string' ? res.data.message : res.data.message?.text || res.data.text || '';
        let newSubject = item.subject;
        const subjectMatch = newBody.match(/^Subject:\s*(.+?)(?:\n|$)/im);
        if (subjectMatch) {
          newSubject = subjectMatch[1].trim();
          newBody = newBody.replace(/^Subject:\s*.+?(?:\n|$)/im, '').trim();
        }

        setItems(prev => prev.map(i =>
          i.id === itemId ? { ...i, body: newBody.trim(), subject: newSubject, generatedAt: new Date().toISOString() } : i
        ));
        if (selectedItem?.id === itemId) {
          setSelectedItem(prev => ({ ...prev, body: newBody.trim(), subject: newSubject }));
        }
      }
    } catch (err) {
      console.error('Regenerate failed:', err);
    } finally {
      setIsRegenerating(false);
    }
  };

  // Generate initial email for a lead that was added to Reach without a draft
  const handleGenerate = async (itemId, researchData) => {
    setIsRegenerating(true);
    try {
      const item = items.find(i => i.id === itemId);
      const c = extractContact(item);
      const angle = item.angle || 'sell';
      const angleInfo = ANGLES.find(a => a.id === angle);

      const res = await api.post('/api/ai/generate-outreach', {
        contact: { name: c.name, title: c.title, company: c.company, industry: c.industry, location: c.location },
        intent: angle,
        outreachContext: angleInfo?.context || 'sales',
        generateMultiple: false,
        leadId: itemId,
        hook: researchData?.suggestedOpener || researchData?.whyNow || item.hook,
        hookType: item.hookType,
        webContext: {
          recentNews: researchData?.whyNow || '',
          openPositions: researchData?.openJobs ? `${researchData.openJobs} open positions` : '',
          isHiring: researchData?.openJobs > 0,
        },
      });

      if (res.data?.success) {
        let newBody = typeof res.data.message === 'string' ? res.data.message : res.data.message?.text || res.data.text || '';
        let newSubject = '';
        const subjectMatch = newBody.match(/^Subject:\s*(.+?)(?:\n|$)/im);
        if (subjectMatch) {
          newSubject = subjectMatch[1].trim();
          newBody = newBody.replace(/^Subject:\s*.+?(?:\n|$)/im, '').trim();
        }

        setItems(prev => prev.map(i =>
          i.id === itemId ? { ...i, body: newBody.trim(), subject: newSubject, generatedAt: new Date().toISOString() } : i
        ));
        if (selectedItem?.id === itemId) {
          setSelectedItem(prev => ({ ...prev, body: newBody.trim(), subject: newSubject }));
        }
      }
    } catch (err) {
      console.error('Generate failed:', err);
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleAngleChange = async (itemId, newAngle) => {
    setItems(prev => prev.map(i =>
      i.id === itemId ? { ...i, angle: newAngle } : i
    ));
    if (selectedItem?.id === itemId) {
      setSelectedItem(prev => ({ ...prev, angle: newAngle }));
    }
    setIsRegenerating(true);
    try {
      const item = items.find(i => i.id === itemId);
      const c = extractContact(item);
      const angleInfo = ANGLES.find(a => a.id === newAngle);
      const insights = item.leadData?.insights || {};
      const res = await api.post('/api/ai/generate-outreach', {
        contact: { name: c.name, title: c.title, company: c.company, industry: c.industry, location: c.location },
        intent: newAngle,
        outreachContext: angleInfo?.context || 'sales',
        generateMultiple: false,
        leadId: itemId,
        hook: item.hook,
        hookType: item.hookType,
        webContext: {
          recentNews: insights.newsSummary || '',
          openPositions: insights.totalJobs ? `${insights.totalJobs} open positions` : '',
          isHiring: insights.totalJobs > 0,
        },
      });
      if (res.data?.success) {
        let newBody = typeof res.data.message === 'string' ? res.data.message : res.data.message?.text || res.data.text || '';
        let newSubject = item.subject;
        const subjectMatch = newBody.match(/^Subject:\s*(.+?)(?:\n|$)/im);
        if (subjectMatch) {
          newSubject = subjectMatch[1].trim();
          newBody = newBody.replace(/^Subject:\s*.+?(?:\n|$)/im, '').trim();
        }
        setItems(prev => prev.map(i =>
          i.id === itemId ? { ...i, body: newBody.trim(), subject: newSubject, angle: newAngle } : i
        ));
        if (selectedItem?.id === itemId) {
          setSelectedItem(prev => ({ ...prev, body: newBody.trim(), subject: newSubject, angle: newAngle }));
        }
      }
    } catch (err) {
      console.error('Angle regenerate failed:', err);
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleEdit = (itemId, subject, body) => {
    setItems(prev => prev.map(i => i.id === itemId ? { ...i, subject, body } : i));
    if (selectedItem?.id === itemId) {
      setSelectedItem(prev => ({ ...prev, subject, body }));
    }
    // Persist to backend
    api.patch(`/api/ai/outreach/${itemId}/draft`, { subject, body }).catch(err => {
      console.warn('Failed to save draft edits:', err.message);
    });
  };

  // ---------------------------------------------------------------------------
  // KEYBOARD SHORTCUTS
  // ---------------------------------------------------------------------------
  const handlersRef = useRef({ handleRegenerate, handleSkip, handleSend });
  useEffect(() => {
    handlersRef.current = { handleRegenerate, handleSkip, handleSend };
  });

  const editTriggerRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      const currentIdx = filteredItems.findIndex(i => i.id === selectedItem?.id);

      switch (e.key) {
        case 'j':
        case 'ArrowDown': {
          e.preventDefault();
          const next = filteredItems[currentIdx + 1];
          if (next) setSelectedItem(next);
          break;
        }
        case 'k':
        case 'ArrowUp': {
          e.preventDefault();
          const prev = filteredItems[currentIdx - 1];
          if (prev) setSelectedItem(prev);
          break;
        }
        case 'e': {
          if (selectedItem && selectedItem.status === 'draft') {
            e.preventDefault();
            editTriggerRef.current?.();
          }
          break;
        }
        case 'r': {
          if (!e.metaKey && !e.ctrlKey && selectedItem) {
            e.preventDefault();
            handlersRef.current.handleRegenerate(selectedItem.id);
          }
          break;
        }
        case 'x': {
          if (selectedItem && selectedItem.status === 'draft') {
            e.preventDefault();
            handlersRef.current.handleSkip(selectedItem.id);
          }
          break;
        }
        case 'Enter': {
          if ((e.metaKey || e.ctrlKey) && selectedItem && selectedItem.status === 'draft') {
            e.preventDefault();
            // Programmatic mailto for keyboard shortcut
            const sc = extractContact(selectedItem);
            if (sc.email) {
              const url = `mailto:${sc.email}?subject=${encodeURIComponent(selectedItem.subject || '')}&body=${encodeURIComponent(selectedItem.body || '')}`;
              window.open(url, '_blank');
            }
            handlersRef.current.handleSend(selectedItem.id);
          }
          break;
        }
        case 'Escape': {
          if (activeCampaign) {
            setActiveCampaign(null);
            setSelectedItem(null);
          }
          break;
        }
        default:
          break;
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [filteredItems, selectedItem, activeCampaign]);

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------
  const totalPipeline = stats.researching + stats.draft + stats.sent + stats.opened;

  // LANDING PAGE: show campaigns
  if (!activeCampaign && !isLoading && !error) {
    return (
      <div
        className="h-screen flex flex-col bg-[#f8f8f6]"
        style={{ marginLeft: isMobile ? '0' : 'var(--sidebar-width, 64px)', paddingTop: '56px' }}
      >
        <div className="bg-white border-b border-slate-100 flex-shrink-0">
          <div className="px-4 sm:px-6 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div className="flex items-center gap-3 sm:gap-4 min-w-0">
              <h1 className="text-lg sm:text-xl font-bold text-slate-900 flex items-center gap-2 flex-shrink-0" style={{ fontFamily: "'Playfair Display', serif" }}>
                Reach
                <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-[#5a20ff]/10 text-[#5a20ff] rounded-md">Beta</span>
              </h1>
              <div className="hidden sm:block">
                <PipelineBar stats={stats} activeStage={activeStage} onStageClick={setActiveStage} />
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button onClick={fetchData} disabled={isLoading}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
                <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              </button>
              <button onClick={() => navigate('/app')}
                className="flex items-center gap-2 px-4 py-2 bg-[#5a20ff] text-white text-sm font-medium rounded-lg hover:bg-[#4a18d9] transition-colors">
                <Plus className="w-4 h-4" /> <span className="hidden sm:inline">New</span> Reach
              </button>
            </div>
          </div>
          {/* Mobile pipeline bar */}
          <div className="sm:hidden px-4 pb-2">
            <PipelineBar stats={stats} activeStage={activeStage} onStageClick={setActiveStage} />
          </div>
          {totalPipeline > 0 && (
            <div className="px-4 sm:px-6 pb-3 flex items-center gap-4 sm:gap-6 text-xs text-slate-500">
              <span className="flex items-center gap-1">
                <span className="font-semibold text-[#5a20ff]">{stats.draft}</span> draft{stats.draft !== 1 ? 's' : ''}
              </span>
              <span className="flex items-center gap-1">
                <span className="font-semibold text-blue-500">{stats.sent}</span> sent
              </span>
              {stats.opened > 0 && (
                <span className="flex items-center gap-1">
                  <span className="font-semibold text-emerald-500">{stats.opened}</span> opened
                </span>
              )}
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {!hasBrandVoice && !brandNudgeDismissed && (
            <div className="mb-4 p-4 bg-[#5a20ff]/5 border border-[#5a20ff]/15 rounded-xl flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Sparkles className="w-5 h-5 text-[#5a20ff] flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-slate-800">Add your brand voice for better emails</p>
                  <p className="text-xs text-slate-500 mt-0.5">Tell the AI about your company, tone, and value props so outreach sounds like you.</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                <button
                  onClick={() => navigate('/dashboard/settings?tab=brand-voice')}
                  className="px-3 py-1.5 text-xs font-medium bg-[#5a20ff] text-white rounded-lg hover:bg-[#4a18d9] transition-colors"
                >
                  Set up
                </button>
                <button
                  onClick={() => setBrandNudgeDismissed(true)}
                  className="px-2 py-1.5 text-xs text-slate-400 hover:text-slate-600 transition-colors"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}
          {campaigns.length === 0 && totalPipeline === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#5a20ff] to-[#52dcb3] flex items-center justify-center mx-auto mb-4">
                <Send className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                No outreach yet
              </h2>
              <p className="text-sm text-slate-500 mb-4 max-w-sm">
                Search for leads in Scout, like them, and they'll appear here with AI-generated personalized messages.
              </p>
              <button onClick={() => navigate('/app')}
                className="px-4 py-2.5 bg-[#5a20ff] text-white font-medium rounded-lg hover:bg-[#4a18d9] transition-colors">
                Find Leads in Scout
              </button>
            </div>
          ) : (
            <div>
              {/* View All button */}
              {totalPipeline > 0 && (
                <button
                  onClick={() => { setActiveCampaign('all'); setSelectedItem(items[0] || null); }}
                  className="w-full text-left p-5 bg-white rounded-xl border border-slate-200 hover:border-[#5a20ff]/30 hover:shadow-md transition-all group mb-4"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#5a20ff] to-[#52dcb3] flex items-center justify-center">
                        <Send className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-slate-900 group-hover:text-[#5a20ff] transition-colors">
                          All Messages
                        </h3>
                        <p className="text-xs text-slate-500">{totalPipeline} total across all campaigns</p>
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-[#5a20ff] transition-colors" />
                  </div>
                </button>
              )}

              {/* Campaign cards */}
              <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-3">Campaigns</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {campaigns.map(c => (
                  <CampaignCard
                    key={c.id}
                    campaign={c}
                    onClick={() => {
                      setActiveCampaign(c.id);
                      const firstItem = items.find(i => String(i.savedSearchId) === String(c.id));
                      setSelectedItem(firstItem || null);
                    }}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // When selecting an item on mobile, show the preview pane
  const handleSelectItemMobile = (item) => {
    setSelectedItem(item);
    if (isMobile) setMobileShowPreview(true);
  };

  // REVIEW / TABLE VIEW (inside a campaign or "all")
  return (
    <div
      className="h-screen flex flex-col bg-[#f8f8f6]"
      style={{ marginLeft: isMobile ? '0' : 'var(--sidebar-width, 64px)', paddingTop: '56px' }}
    >
      {/* TOP BAR */}
      <div className="bg-white border-b border-slate-100 flex-shrink-0">
        <div className="px-4 sm:px-6 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <button
              onClick={() => { setActiveCampaign(null); setSelectedItem(null); setMobileShowPreview(false); }}
              className="flex items-center gap-1 text-sm text-slate-500 hover:text-[#5a20ff] transition-colors flex-shrink-0"
            >
              <ChevronLeft className="w-4 h-4" /> <span className="hidden sm:inline">Reach</span> <span className="hidden sm:inline px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wider bg-[#5a20ff]/10 text-[#5a20ff] rounded">Beta</span>
            </button>
            <span className="text-slate-300 hidden sm:inline">/</span>
            <h1 className="text-base sm:text-lg font-bold text-slate-900 truncate" style={{ fontFamily: "'Playfair Display', serif" }}>
              {activeCampaign === 'all'
                ? 'All Messages'
                : campaigns.find(c => String(c.id) === String(activeCampaign))?.name || 'Campaign'}
            </h1>
            <div className="hidden md:block">
              <PipelineBar stats={stats} activeStage={activeStage} onStageClick={setActiveStage} />
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="hidden sm:flex items-center bg-slate-100 rounded-lg p-0.5">
              <button
                onClick={() => setViewMode('review')}
                className={`p-1.5 rounded-md transition-all ${viewMode === 'review' ? 'bg-white shadow-sm text-[#5a20ff]' : 'text-slate-400 hover:text-slate-600'}`}
                title="Review mode"
              >
                <LayoutList className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`p-1.5 rounded-md transition-all ${viewMode === 'table' ? 'bg-white shadow-sm text-[#5a20ff]' : 'text-slate-400 hover:text-slate-600'}`}
                title="Table mode"
              >
                <Table2 className="w-4 h-4" />
              </button>
            </div>

            <button onClick={fetchData} disabled={isLoading}
              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            </button>

            <button onClick={() => navigate('/app')}
              className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-[#5a20ff] text-white text-sm font-medium rounded-lg hover:bg-[#4a18d9] transition-colors">
              <Plus className="w-4 h-4" /> <span className="hidden sm:inline">New Reach</span>
            </button>
          </div>
        </div>

        {/* Mobile pipeline bar */}
        <div className="md:hidden px-4 pb-2">
          <PipelineBar stats={stats} activeStage={activeStage} onStageClick={setActiveStage} />
        </div>

        {totalPipeline > 0 && (
          <div className="px-4 sm:px-6 pb-3 flex items-center gap-4 sm:gap-6 text-xs text-slate-500">
            <span className="flex items-center gap-1">
              <span className="font-semibold text-[#5a20ff]">{stats.draft}</span> draft{stats.draft !== 1 ? 's' : ''}
            </span>
            <span className="flex items-center gap-1">
              <span className="font-semibold text-blue-500">{stats.sent}</span> sent
            </span>
            {stats.opened > 0 && (
              <span className="flex items-center gap-1">
                <span className="font-semibold text-emerald-500">{stats.opened}</span> opened
              </span>
            )}
          </div>
        )}
      </div>

      {/* CONTENT */}
      {isLoading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-8 h-8 text-[#5a20ff] animate-spin mx-auto mb-3" />
            <p className="text-sm text-slate-500">Loading your outreach pipeline...</p>
          </div>
        </div>
      ) : error ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-sm text-red-500 mb-2">{error}</p>
            <button onClick={fetchData} className="text-sm text-[#5a20ff] hover:underline">Try again</button>
          </div>
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center max-w-sm">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#5a20ff] to-[#52dcb3] flex items-center justify-center mx-auto mb-4">
              <Send className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              {activeStage === 'all' ? 'No messages in this campaign' : `No ${activeStage} items`}
            </h2>
            <p className="text-sm text-slate-500 mb-4">
              {activeStage !== 'all'
                ? 'Try selecting a different pipeline stage.'
                : 'Like leads in Scout and they\'ll appear here.'}
            </p>
            {activeStage !== 'all' && (
              <button onClick={() => setActiveStage('all')} className="text-sm text-[#5a20ff] hover:underline">
                View all
              </button>
            )}
          </div>
        </div>
      ) : viewMode === 'table' ? (
        <div className="flex-1 overflow-auto bg-white">
          <TableView
            items={filteredItems}
            selectedId={selectedItem?.id}
            onSelect={(item) => { setSelectedItem(item); setViewMode('review'); }}
          />
        </div>
      ) : (
        <div className="flex-1 flex min-h-0">
          {/* Left pane: queue — hidden on mobile when preview is shown */}
          <div ref={queueRef} className={`${isMobile && mobileShowPreview ? 'hidden' : 'flex'} ${isMobile ? 'w-full' : 'w-80'} flex-shrink-0 border-r border-slate-200 bg-white overflow-y-auto flex-col`}>
            <div className="px-4 py-3 border-b border-slate-100 sticky top-0 bg-white z-10">
              <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">
                {activeStage === 'all' ? 'All' : STAGES.find(s => s.id === activeStage)?.label || activeStage}
                {' · '}{filteredItems.length}
              </p>
            </div>
            {filteredItems.map(item => (
              <QueueItem
                key={item.id}
                item={item}
                isSelected={selectedItem?.id === item.id}
                onClick={() => handleSelectItemMobile(item)}
              />
            ))}
          </div>

          {/* Right pane: email preview — full-width on mobile */}
          <div className={`${isMobile && !mobileShowPreview ? 'hidden' : 'flex'} flex-1 bg-white overflow-hidden flex-col`}>
            {selectedItem ? (
              <EmailPreview
                item={selectedItem}
                onEdit={handleEdit}
                onRegenerate={handleRegenerate}
                onGenerate={handleGenerate}
                onSend={handleSend}
                onSkip={handleSkip}
                onAngleChange={handleAngleChange}
                isRegenerating={isRegenerating}
                onBack={isMobile ? () => setMobileShowPreview(false) : undefined}
              />
            ) : (
              <div className="h-full flex items-center justify-center text-slate-400 text-sm">
                Select a lead from the queue
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
